/**
 * Custom table pivot implementation for paginated ResultSets
 *
 * This is necessary because Cube.js's tablePivot() function performs aggregation
 * and grouping that requires the complete dataset. When applied to partial
 * ResultSets from pagination, it produces incorrect results.
 *
 * This implementation combines raw data from all ResultSets first, then applies
 * the same pivot logic that Cube.js would normally apply, based on the actual
 * Cube.js source code implementation.
 */
/**
 * Performs table pivot operation on combined data from multiple ResultSets
 * Based on the actual Cube.js ResultSet.tablePivot() implementation
 */
export const tablePivotForPaginatedData = (resultSets, pivotConfig) => {
    if (!resultSets || resultSets.length === 0) {
        return [];
    }
    // If no pivot config specified, return combined raw data
    if (!pivotConfig) {
        return resultSets.flatMap((rs) => rs?.rawData() || []);
    }
    // Apply the actual Cube.js tablePivot logic to combined data
    return tablePivotOnCombinedData(resultSets, pivotConfig);
};
/**
 * Implements the core tablePivot logic by creating a synthetic ResultSet with combined data
 * and letting the original Cube.js tablePivot method handle the transformation
 */
const tablePivotOnCombinedData = (resultSets, pivotConfig) => {
    const firstResultSet = resultSets[0];
    if (!firstResultSet) {
        return [];
    }
    try {
        // Create a synthetic ResultSet with combined raw data
        const combinedRawData = resultSets.flatMap((rs) => rs?.rawData() || []);
        // Create a synthetic loadResponse with the same structure as the original
        const originalLoadResponse = firstResultSet.loadResponse || firstResultSet;
        const syntheticLoadResponse = {
            ...originalLoadResponse,
            results: [
                {
                    ...(originalLoadResponse.results?.[0] || originalLoadResponse),
                    data: combinedRawData,
                    // Keep the original query and annotations
                    query: originalLoadResponse.results?.[0]?.query || originalLoadResponse.query,
                    annotation: originalLoadResponse.results?.[0]?.annotation || originalLoadResponse.annotation,
                },
            ],
        };
        // Create a new ResultSet with the combined data
        const ResultSetClass = firstResultSet.constructor;
        const syntheticResultSet = new ResultSetClass(syntheticLoadResponse);
        // Use the original tablePivot method on the synthetic ResultSet
        const result = syntheticResultSet.tablePivot(pivotConfig);
        return result;
    }
    catch (error) {
        console.error('Error creating synthetic ResultSet, falling back to individual pivots:', error);
        // Fallback to individual ResultSet pivots if synthetic approach fails
        return resultSets.flatMap((rs) => rs?.tablePivot(pivotConfig) || []);
    }
};
/**
 * Creates a mock ResultSet that can be used to apply tablePivot on combined data
 * This preserves the original Cube.js pivot logic while working with our combined dataset
 */
export const createCombinedResultSetForPivot = (resultSets, pivotConfig) => {
    if (!resultSets || resultSets.length === 0) {
        return {
            tablePivot: () => [],
            rawData: () => [],
            annotation: () => ({}),
            tableColumns: () => [],
        };
    }
    const combinedRawData = resultSets.flatMap((rs) => rs?.rawData() || []);
    const firstResultSet = resultSets[0];
    return {
        tablePivot: (config) => {
            const actualPivotConfig = config || pivotConfig;
            if (!actualPivotConfig) {
                return combinedRawData;
            }
            // Use our custom tablePivot implementation based on Cube.js source
            return tablePivotOnCombinedData(resultSets, actualPivotConfig);
        },
        rawData: () => combinedRawData,
        annotation: () => firstResultSet?.annotation() || {},
        tableColumns: (config) => firstResultSet?.tableColumns(config) || [],
    };
};
/**
 * Determines if pivot should be applied based on pivot configuration
 */
export const shouldUsePivot = (pivotConfig) => {
    return !!(pivotConfig &&
        ((pivotConfig.x && pivotConfig.x.length > 0) ||
            (pivotConfig.y && pivotConfig.y.length > 0) ||
            pivotConfig.fillMissingDates === true));
};
/**
 * Combines series data from multiple ResultSets with proper aggregation
 * This ensures that data points with the same x-value are summed rather than duplicated
 *
 * @param resultSets - Array of Cube.js ResultSet objects from paginated queries
 * @param pivotConfig - Pivot configuration to pass to each ResultSet.series() call
 * @returns Combined series array with properly aggregated data points
 */
export const combineSeriesFromResultSets = (resultSets, pivotConfig) => {
    if (!resultSets || resultSets.length === 0) {
        return [];
    }
    // Get all series from all ResultSets
    const allSeries = resultSets.flatMap((rs) => rs?.series(pivotConfig) || []);
    if (allSeries.length === 0) {
        return [];
    }
    // Group series by key
    const seriesMap = new Map();
    allSeries.forEach((series) => {
        if (!seriesMap.has(series.key)) {
            seriesMap.set(series.key, {
                ...series,
                series: [...series.series], // Copy the series data
            });
        }
        else {
            // Properly merge series data by x-value to avoid duplicates and ensure correct aggregation
            const existingSeries = seriesMap.get(series.key);
            const existingDataMap = new Map();
            // Create a map of existing data points by their x value
            existingSeries.series.forEach((dataPoint) => {
                existingDataMap.set(dataPoint.x, dataPoint);
            });
            // Merge new series data points
            series.series.forEach((dataPoint) => {
                if (existingDataMap.has(dataPoint.x)) {
                    // Sum values for the same x-coordinate (for proper aggregation)
                    const existing = existingDataMap.get(dataPoint.x);
                    existing.value = (parseFloat(existing.value) || 0) + (parseFloat(dataPoint.value) || 0);
                }
                else {
                    // Add new data point
                    existingDataMap.set(dataPoint.x, { ...dataPoint });
                }
            });
            // Convert back to array, maintaining proper order
            existingSeries.series = Array.from(existingDataMap.values()).sort((a, b) => {
                // Try to sort by x value (works for dates, numbers, etc.)
                if (a.x < b.x) {
                    return -1;
                }
                if (a.x > b.x) {
                    return 1;
                }
                return 0;
            });
        }
    });
    return Array.from(seriesMap.values());
};
/**
 * Aggregates series data from multiple ResultSets by summing values for the same series key
 * This is specifically designed for chart types like number charts and horizontal bar charts
 * where each series represents a total value that should be summed across pages
 *
 * @param resultSets - Array of Cube.js ResultSet objects from paginated queries
 * @param pivotConfig - Pivot configuration to pass to each ResultSet.series() call
 * @returns Aggregated series array with summed values
 */
export const aggregateSeriesFromResultSets = (resultSets, pivotConfig) => {
    if (!resultSets || resultSets.length === 0) {
        return [];
    }
    // Get all series from all ResultSets
    const allSeries = resultSets.flatMap((rs) => rs?.series(pivotConfig) || []);
    if (allSeries.length === 0) {
        return [];
    }
    // Group series by key and sum values
    const seriesMap = new Map();
    allSeries.forEach((series) => {
        if (!seriesMap.has(series.key)) {
            seriesMap.set(series.key, {
                ...series,
                series: series.series.map((item) => ({ ...item })), // Copy series data
            });
        }
        else {
            // Sum the values for the same series key
            const existingSeries = seriesMap.get(series.key);
            series.series.forEach((item, index) => {
                if (existingSeries.series[index]) {
                    existingSeries.series[index].value =
                        (parseFloat(existingSeries.series[index].value) || 0) + (parseFloat(item.value) || 0);
                }
                else {
                    existingSeries.series.push({ ...item });
                }
            });
        }
    });
    return Array.from(seriesMap.values());
};
